import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Select,
  AutoComplete,
  Switch,
  Avatar,
  Spin,
  Alert,
  Popconfirm,
  Dropdown,
} from 'antd';
import {
  TeamOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  PhoneOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { callCenterAgentsApi, show422Error, ValidationError } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { toJalali } from '../../utils/dateUtils';

const STATUS_CONFIG = {
  available: { label: 'در دسترس', color: 'green' },
  break: { label: 'استراحت', color: 'orange' },
  meeting: { label: 'جلسه', color: 'blue' },
  offline: { label: 'آفلاین', color: 'default' },
};

const formatStatus = (status) => {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <Tag>{status || '—'}</Tag>;
  return <Tag color={cfg.color} className="custom-font">{cfg.label}</Tag>;
};

const CallCenterAgents = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [loadingCreateMeta, setLoadingCreateMeta] = useState(false);
  const [createEmployees, setCreateEmployees] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [asteriskSynced, setAsteriskSynced] = useState(true);
  const [asteriskMessage, setAsteriskMessage] = useState(null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callCenterAgentsApi.getAll();
      setAgents(res?.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت اپراتورها:', error);
      message.error('خطا در دریافت لیست اپراتورها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // یک کاربر می‌تواند بیش از یک داخلی داشته باشد؛ بنابراین کارمندان قبلی فیلتر نمی‌شوند
  const availableEmployees = useMemo(
    () => createEmployees,
    [createEmployees],
  );

  const availableExtensions = useMemo(
    () => extensions.filter((ext) => !ext.assigned_user_id),
    [extensions],
  );

  const openAddModal = async () => {
    setAddOpen(true);
    addForm.resetFields();
    addForm.setFieldsValue({ is_active: true });
    setLoadingCreateMeta(true);
    try {
      const res = await callCenterAgentsApi.getCreateForm();
      const data = res?.data ?? {};
      setCreateEmployees(data.employees ?? []);
      setExtensions(data.extensions ?? []);
      setQueues(data.queues ?? []);
      setAsteriskSynced(data.asterisk_synced !== false);
      setAsteriskMessage(data.asterisk_message ?? null);
    } catch (error) {
      console.error('خطا در دریافت فرم اپراتور:', error);
      message.error('خطا در دریافت اطلاعات فرم');
      setAddOpen(false);
    } finally {
      setLoadingCreateMeta(false);
    }
  };

  const submitAddAgent = async () => {
    try {
      const values = await addForm.validateFields();
      setAddSubmitting(true);
      await callCenterAgentsApi.create({
        user_id: values.user_id,
        extension: values.extension,
        queue: values.queue,
        is_active: values.is_active,
      });
      message.success('اپراتور با موفقیت ثبت شد');
      setAddOpen(false);
      await loadAgents();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در ثبت اپراتور:', error);
      if (error instanceof ValidationError) {
        show422Error(error, addForm);
      } else {
        message.error(error?.message || 'خطا در ثبت اپراتور');
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteAgent = async (row) => {
    setDeleteLoadingId(row.id);
    try {
      await callCenterAgentsApi.remove(row.id);
      message.success('اپراتور حذف شد');
      await loadAgents();
    } catch (error) {
      console.error('خطا در حذف اپراتور:', error);
      message.error(error?.message || 'خطا در حذف اپراتور');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const changeAgentStatus = async (row, newStatus) => {
    if (row.status === newStatus) return;
    setStatusLoadingId(row.id);
    try {
      await callCenterAgentsApi.updateStatus({
        status: newStatus,
        agent_extension_id: row.id,
      });
      message.success(`وضعیت به «${STATUS_CONFIG[newStatus]?.label || newStatus}» تغییر کرد`);
      await loadAgents();
    } catch (error) {
      console.error('خطا در تغییر وضعیت:', error);
      message.error(error?.message || 'خطا در تغییر وضعیت اپراتور');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'اپراتور',
      key: 'user',
      render: (_, row) => {
        const user = row.user || {};
        return (
          <Space>
            <Avatar src={getImagePath(user.image_url || user.image)} size={40}>
              {(user.name || '?').charAt(0)}
            </Avatar>
            <div>
              <div className="custom-font font-medium">{user.name || '—'}</div>
              <div className="custom-font text-xs text-gray-500">{user.email || ''}</div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'داخلی',
      dataIndex: 'extension',
      key: 'extension',
      width: 100,
      render: (ext) => (
        <Tag icon={<PhoneOutlined />} className="custom-font" dir="ltr">
          {ext || '—'}
        </Tag>
      ),
    },
    {
      title: 'صف',
      dataIndex: 'queue',
      key: 'queue',
      width: 120,
      render: (queue) => (
        <span className="custom-font" dir="ltr">
          {queue || '—'}
        </span>
      ),
    },
    {
      title: 'وضعیت حضور',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status, row) => {
        const items = Object.entries(STATUS_CONFIG).map(([key, cfg]) => ({
          key,
          label: (
            <Space>
              <Tag color={cfg.color} className="custom-font" style={{ margin: 0 }}>{cfg.label}</Tag>
              {key === status && <span className="text-xs text-gray-400">✓</span>}
            </Space>
          ),
        }));
        return (
          <Dropdown
            menu={{ items, onClick: ({ key }) => changeAgentStatus(row, key) }}
            trigger={['click']}
          >
            <Button
              type="text"
              size="small"
              loading={statusLoadingId === row.id}
              className="custom-font"
            >
              {formatStatus(status)} <DownOutlined style={{ fontSize: 10, marginRight: 4 }} />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: 'فعال',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active) => (
        <Tag color={active ? 'green' : 'default'} className="custom-font">
          {active ? 'فعال' : 'غیرفعال'}
        </Tag>
      ),
    },
    {
      title: 'آخرین تغییر وضعیت',
      dataIndex: 'status_changed_at',
      key: 'status_changed_at',
      width: 180,
      render: (dt) => {
        if (!dt) return <span className="text-gray-400">—</span>;
        const jalali = toJalali(dt, 'jYYYY/jMM/jDD');
        return (
          <span className="custom-font text-sm text-gray-600 dark:text-gray-400">
            {jalali || dt}
          </span>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <PermissionGate
          permission="manage_callcenter_setting"
          fallback={<span className="text-gray-400 text-sm">—</span>}
        >
          <Popconfirm
            title="حذف این اپراتور؟"
            description="داخلی از این کارمند جدا می‌شود."
            okText="بله"
            cancelText="خیر"
            onConfirm={() => deleteAgent(row)}
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteLoadingId === row.id}
            >

            </Button>
          </Popconfirm>
        </PermissionGate>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                <TeamOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  اپراتورها
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  مدیریت اپراتورهای مرکز تماس و تخصیص داخلی سانترال (Asterisk)
                </p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={loadAgents} loading={loading}>
                بروزرسانی
              </Button>
              <PermissionGate permission="manage_callcenter_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openAddModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن اپراتور
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={5} columns={6} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={agents}
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
                locale={{ emptyText: 'اپراتوری ثبت نشده است' }}
              />
            )}
          </div>
        </Card>

        <Modal
          title="افزودن اپراتور"
          open={addOpen}
          onCancel={() => setAddOpen(false)}
          onOk={submitAddAgent}
          confirmLoading={addSubmitting}
          destroyOnClose
          okText="ثبت"
          cancelText="انصراف"
          width={520}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingCreateMeta}>
            {!asteriskSynced && asteriskMessage && (
              <Alert
                type="info"
                showIcon
                className="mb-4 custom-font"
                message={asteriskMessage}
              />
            )}
            <Form form={addForm} layout="vertical" className="custom-font" style={{ marginTop: 8 }}>
              <Form.Item
                name="user_id"
                label="کارمند"
                rules={[{ required: true, message: 'کارمند را انتخاب کنید' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="انتخاب کارمند"
                  popupClassName="custom-font"
                  className="custom-font"
                  options={availableEmployees.map((e) => {
                    const currentExts =
                      e.assigned_extensions?.length > 0
                        ? e.assigned_extensions
                        : (e.agent_extensions || []).map((x) => x.extension).filter(Boolean);
                    const extLabel = currentExts.length > 0 ? ` (داخلی‌های فعلی: ${currentExts.join(', ')})` : '';
                    return {
                      value: e.id,
                      label: `${e.name} — ${e.email}${extLabel}`,
                    };
                  })}
                  notFoundContent={
                    <span className="custom-font text-gray-500">کارمندی یافت نشد</span>
                  }
                />
              </Form.Item>

              <Form.Item
                name="extension"
                label="داخلی سانترال / استریسک"
                rules={[{ required: true, message: 'داخلی را وارد یا انتخاب کنید' }]}
                extra="می‌توانید داخلی را از لیست انتخاب کرده یا شماره جدید تایپ کنید (مثال: 101)"
              >
                <AutoComplete
                  placeholder="مثال: 101 یا انتخاب از لیست"
                  className="custom-font w-full"
                  popupClassName="custom-font"
                  options={availableExtensions.map((ext) => ({
                    value: String(ext.extension),
                    label: `${ext.extension}${ext.name && ext.name !== ext.extension ? ` — ${ext.name}` : ''}${ext.channel ? ` (${ext.channel})` : ''}`,
                  }))}
                  filterOption={(inputValue, option) =>
                    String(option?.value || '').toLowerCase().includes(inputValue.toLowerCase()) ||
                    String(option?.label || '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="queue"
                label="صف تماس (Queue)"
                rules={[{ required: false }]}
              >
                <AutoComplete
                  placeholder="مثال: sales یا انتخاب از لیست"
                  className="custom-font w-full"
                  popupClassName="custom-font"
                  options={(queues || []).map((q) => ({ value: q, label: q }))}
                  filterOption={(inputValue, option) =>
                    String(option?.value || '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="is_active"
                label="فعال"
                valuePropName="checked"
              >
                <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CallCenterAgents;
